import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CaretLeft, CaretRight, Printer, PencilSimple, Trash, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

// -- Date helpers ------------------------------------------------------------
const iso = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// Monday of the week that contains `d`
const weekStart = (d) => {
  const x = new Date(d); const day = x.getDay(); // 0..6, 0=Sun
  const diff = (day + 6) % 7; // Mon=0
  x.setDate(x.getDate() - diff); x.setHours(0, 0, 0, 0);
  return x;
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Schedule() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";

  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);
  const from = iso(days[0]); const to = iso(days[6]);
  // Default "Print for date" = tomorrow so a manager on Friday can preload Mon's cards
  const [printDate, setPrintDate] = useState(() => iso(addDays(new Date(), 1)));

  const [staff, setStaff] = useState([]);
  const [operations, setOperations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [editing, setEditing] = useState(null); // { staff, date }

  const load = async () => {
    const [s, o, sch] = await Promise.all([
      api.get("/staff"),
      api.get("/operations"),
      api.get(`/schedules?date_from=${from}&date_to=${to}`),
    ]);
    setStaff(s.data.filter((x) => x.active));
    setOperations(o.data.filter((x) => x.active));
    setSchedules(sch.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const byKey = useMemo(() => {
    const m = new Map();
    for (const s of schedules) m.set(`${s.staff_id}|${s.date}`, s);
    return m;
  }, [schedules]);

  const shiftWeek = (n) => setAnchor(addDays(anchor, n * 7));
  const goToday = () => setAnchor(weekStart(new Date()));

  const [copyBusy, setCopyBusy] = useState(false);
  const copyFromPrev = async () => {
    const prevMon = iso(addDays(anchor, -7));
    const currMon = iso(anchor);
    const overwrite = window.confirm(
      `Copy schedules from the previous week (starting ${prevMon}) to this week (starting ${currMon})?\n\nOK = overwrite any existing cells this week.\nCancel = skip existing cells (safer).`
    );
    // If user hits Cancel we still copy — just non-destructive. If they close, nothing.
    // Distinguish "close" via an explicit second prompt is heavy; treat Cancel as "skip existing".
    setCopyBusy(true);
    try {
      const r = await api.post("/schedules/copy-week", {
        source_start: prevMon, dest_start: currMon, overwrite,
      });
      if (r.data.copied === 0 && r.data.skipped === 0) {
        toast.error("Previous week is empty — nothing to copy");
      } else {
        toast.success(`Copied ${r.data.copied}${r.data.skipped ? `, skipped ${r.data.skipped}` : ""}`);
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCopyBusy(false); }
  };

  const removeSched = async (id) => {
    if (!window.confirm("Remove this schedule slot?")) return;
    try { await api.delete(`/schedules/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Weekly Schedule</h1>
          <p className="text-xs text-zinc-600 mt-1">Assign staff to operations for each day. Click a cell to edit.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/schedule/print?date=${iso(new Date())}`} className="brut-btn brut-btn-dark px-3 py-2 inline-flex items-center gap-2" data-testid="print-today-jobcards">
            <Printer size={14} weight="bold" /> Today
          </Link>
          <div className="brut-card p-2 inline-flex items-center gap-2">
            <input
              type="date"
              value={printDate}
              onChange={(e) => setPrintDate(e.target.value)}
              className="brut-input py-1"
              data-testid="print-date-picker"
            />
            <Link
              to={`/schedule/print?date=${printDate}`}
              className="brut-btn brut-btn-primary px-3 py-1 inline-flex items-center gap-1 text-xs"
              data-testid="print-picked-date"
            >
              <Printer size={12} weight="bold" /> Print
            </Link>
          </div>
          <Link
            to={`/schedule/print?from=${from}&to=${to}`}
            className="brut-btn px-3 py-2 inline-flex items-center gap-2"
            data-testid="print-whole-week"
            title={`Print all cards for ${from} → ${to}`}
          >
            <Printer size={14} weight="bold" /> Whole Week
          </Link>
          <Link
            to={`/schedule/team-tracker?date=${iso(new Date())}`}
            className="brut-btn px-3 py-2 inline-flex items-center gap-2"
            data-testid="print-team-tracker"
            title="One consolidated sheet with all workers for today"
          >
            <Printer size={14} weight="bold" /> Team Tracker
          </Link>
          <Link
            to={`/schedule/wall-chart?week=${from}`}
            className="brut-btn px-3 py-2 inline-flex items-center gap-2"
            data-testid="print-wall-chart"
            title="Printable 11×17 wall chart for the shop floor"
          >
            <Printer size={14} weight="bold" /> Wall Chart
          </Link>
          <div className="brut-card p-1 flex items-center">
            <button onClick={() => shiftWeek(-1)} className="brut-tag brut-tag-white" data-testid="prev-week"><CaretLeft size={12} weight="bold" /></button>
            <button onClick={goToday} className="brut-tag brut-tag-yellow mx-1 text-xs" data-testid="this-week">This Week</button>
            <button onClick={() => shiftWeek(1)} className="brut-tag brut-tag-white" data-testid="next-week"><CaretRight size={12} weight="bold" /></button>
          </div>
        </div>
      </div>

      <div className="brut-card p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="font-mono-ui text-sm">
          Week: <b>{from}</b> → <b>{to}</b>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {canEdit && (
            <button
              onClick={copyFromPrev}
              disabled={copyBusy}
              className="brut-tag brut-tag-yellow inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="copy-prev-week"
              title="Clone schedules from the previous week to this week"
            >
              <Copy size={11} weight="bold" /> {copyBusy ? "Copying..." : "Copy from previous week"}
            </button>
          )}
          <Link to="/staff" className="brut-tag brut-tag-white underline">Manage Staff</Link>
          <Link to="/operations" className="brut-tag brut-tag-white underline">Manage Operations</Link>
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="schedule-grid">
          <thead>
            <tr>
              <th className="min-w-[180px]">Staff</th>
              {days.map((d, i) => (
                <th key={i} className={`text-left ${iso(d) === iso(new Date()) ? "bg-amber-50" : ""}`}>
                  <div className="font-display uppercase text-xs">{DOW[i]}</div>
                  <div className="font-mono-ui text-[10px] font-normal">{iso(d)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-zinc-500">
                No active staff. <Link to="/staff" className="underline font-bold">Add staff</Link> to build the schedule.
              </td></tr>
            )}
            {staff.map((s) => (
              <tr key={s.id} data-testid={`sched-row-${s.name}`}>
                <td>
                  <div className="font-bold">{s.name}</div>
                  <div className="text-xs text-zinc-600">{s.designation}</div>
                </td>
                {days.map((d, i) => {
                  const dateIso = iso(d);
                  const sched = byKey.get(`${s.id}|${dateIso}`);
                  return (
                    <td key={i} className="align-top">
                      {sched ? (
                        <div className="brut-card p-2 space-y-1" style={{ borderLeft: `6px solid ${(sched.operations && sched.operations[0]?.color) || "#dbeafe"}`, background: "#f8fafc" }}>
                          {(sched.operations && sched.operations.length > 0 ? sched.operations : [{ code: sched.operation_code, name: sched.operation_name }]).map((op, oi) => (
                            <div key={oi}>
                              <div className="font-mono-ui text-[11px] font-bold" style={{ color: op.color || "#0f172a" }}>{op.code}</div>
                              <div className="text-[11px] leading-tight">{op.name}</div>
                            </div>
                          ))}
                          <div className="text-[10px] text-zinc-600 font-mono-ui">{sched.shift_start}–{sched.shift_end}</div>
                          {canEdit && (
                            <div className="flex gap-1 pt-1">
                              <Link
                                to={`/schedule/print?date=${dateIso}&staff_id=${s.id}`}
                                className="brut-tag brut-tag-dark"
                                title="Print this job card"
                                data-testid={`print-cell-${s.name}-${dateIso}`}
                              >
                                <Printer size={9} weight="bold" />
                              </Link>
                              <button onClick={() => setEditing({ staff: s, date: dateIso, existing: sched })} className="brut-tag brut-tag-white" data-testid={`edit-cell-${s.name}-${dateIso}`}>
                                <PencilSimple size={9} weight="bold" />
                              </button>
                              <button onClick={() => removeSched(sched.id)} className="brut-tag brut-tag-red" data-testid={`del-cell-${s.name}-${dateIso}`}>
                                <Trash size={9} weight="bold" />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : canEdit ? (
                        <button onClick={() => setEditing({ staff: s, date: dateIso, existing: null })} className="w-full text-left brut-tag brut-tag-white text-xs" data-testid={`assign-${s.name}-${dateIso}`}>
                          + Assign
                        </button>
                      ) : <span className="text-zinc-400 text-xs">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ScheduleModal
          data={editing}
          operations={operations}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ScheduleModal({ data, operations, onClose, onSaved }) {
  const { staff, date, existing } = data;
  // Multi-op: seed from operation_ids if present, else fall back to legacy operation_id
  const [operationIds, setOperationIds] = useState(() => {
    const seed = existing?.operation_ids && existing.operation_ids.length ? existing.operation_ids : (existing?.operation_id ? [existing.operation_id] : []);
    return seed;
  });
  const [shiftStart, setShiftStart] = useState(existing?.shift_start || "08:00");
  const [shiftEnd, setShiftEnd] = useState(existing?.shift_end || "16:00");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  const toggleOp = (opid) => {
    setOperationIds((cur) => cur.includes(opid) ? cur.filter((x) => x !== opid) : [...cur, opid]);
  };

  // Multi-day: figure out which weekday `date` is, and offer to also apply to other days.
  const baseDay = parseIso(date);
  const monOfWeek = weekStart(baseDay);
  const weekDates = Array.from({ length: 7 }, (_, i) => iso(addDays(monOfWeek, i)));
  const currentDayIdx = weekDates.indexOf(date);
  const [applyDates, setApplyDates] = useState(() => new Set()); // extra dates to also assign

  const toggleApply = (d) => {
    const next = new Set(applyDates);
    if (next.has(d)) next.delete(d); else next.add(d);
    setApplyDates(next);
  };
  const selectAllWeek = () => {
    const next = new Set(weekDates.filter((d) => d !== date));
    setApplyDates(next);
  };
  const clearApply = () => setApplyDates(new Set());

  // Live duration
  const durHours = (() => {
    const toM = (v) => { const [h, m] = (v || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
    const s = toM(shiftStart), e = toM(shiftEnd);
    if (!s || !e || e <= s) return 0;
    return Math.round(((e - s) / 60) * 10) / 10;
  })();
  const outOfRange = (() => {
    const toM = (v) => { const [h, m] = (v || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
    return toM(shiftStart) < 6 * 60 || toM(shiftEnd) > 22 * 60;
  })();

  const save = async (e) => {
    e.preventDefault();
    if (operationIds.length === 0) return toast.error("Pick at least one operation");
    if (outOfRange) return toast.error("Shifts must be between 06:00 and 22:00");
    if (durHours <= 0) return toast.error("Shift end must be after shift start");
    setSaving(true);
    try {
      // Always save the primary date first…
      const targets = [date, ...Array.from(applyDates)];
      const results = await Promise.allSettled(targets.map((d) => api.post("/schedules", {
        staff_id: staff.id, date: d,
        operation_id: operationIds[0],
        operation_ids: operationIds,
        shift_start: shiftStart, shift_end: shiftEnd, notes,
      })));
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (ok > 0) toast.success(`Saved ${ok} day${ok === 1 ? "" : "s"}`);
      if (failed > 0) toast.error(`${failed} failed`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brut-card bg-white w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl uppercase">Assign · {staff.name}</h3>
          <div className="font-mono-ui text-xs">{date}</div>
        </div>
        <form onSubmit={save} className="space-y-3" data-testid="sched-form">
          <div>
            <label className="brut-label">Operations * <span className="text-[10px] text-zinc-500 font-normal">(pick 1 or more; worker may switch as needed)</span></label>
            <div className="mt-1 grid grid-cols-2 gap-1 max-h-40 overflow-y-auto brut-border p-2" data-testid="sched-ops-list">
              {operations.length === 0 && <div className="text-xs text-zinc-500 col-span-2 py-2 text-center">No active operations. Add one on the Operations page.</div>}
              {operations.map((op) => {
                const on = operationIds.includes(op.id);
                return (
                  <label
                    key={op.id}
                    className={`flex items-center gap-2 px-2 py-1 cursor-pointer border-2 text-xs ${on ? "border-blue-600 bg-blue-100" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid={`sched-op-${op.code}`}
                  >
                    <input type="checkbox" checked={on} onChange={() => toggleOp(op.id)} />
                    <div className="min-w-0">
                      <div className="font-mono-ui font-bold">{op.code}</div>
                      <div className="text-[10px] text-zinc-600 truncate">{op.name}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {operationIds.length >= 2 && (
              <div className="text-[10px] text-blue-700 mt-1 font-mono-ui" data-testid="sched-multi-hint">
                {operationIds.length} operations selected · worker can move between them during the shift
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="brut-label">Shift Start</label>
              <input type="time" min="06:00" max="22:00" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} className="brut-input mt-1" data-testid="sched-start" />
            </div>
            <div>
              <label className="brut-label">Shift End</label>
              <input type="time" min="06:00" max="22:00" value={shiftEnd} onChange={(e) => setShiftEnd(e.target.value)} className="brut-input mt-1" data-testid="sched-end" />
            </div>
          </div>
          <div className="text-xs font-mono-ui text-zinc-600" data-testid="sched-duration">
            Duration: <b className={durHours <= 0 || outOfRange ? "text-red-600" : ""}>{durHours} h</b>
            {" "}<span className="text-[10px]">(shifts allowed 06:00–22:00)</span>
          </div>

          <div className="brut-border p-3 space-y-2 bg-blue-50">
            <div className="flex items-center justify-between">
              <div className="brut-label">Also apply to</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={selectAllWeek} className="brut-tag brut-tag-white text-[10px]" data-testid="apply-full-week">Rest of week</button>
                <button type="button" onClick={clearApply} className="brut-tag brut-tag-white text-[10px]" data-testid="apply-clear">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="apply-days">
              {weekDates.map((d, i) => {
                const isCurrent = i === currentDayIdx;
                const on = applyDates.has(d);
                return (
                  <label
                    key={d}
                    className={`flex flex-col items-center gap-0.5 py-1 cursor-pointer text-xs border-2 ${
                      isCurrent ? "border-blue-600 bg-blue-100" : on ? "border-blue-500 bg-blue-100" : "border-slate-300 bg-white hover:border-slate-500"
                    } ${isCurrent ? "cursor-not-allowed" : ""}`}
                    title={d}
                  >
                    <span className="font-display uppercase text-[10px]">{DOW[i]}</span>
                    <span className="font-mono-ui text-[9px] text-zinc-500">{d.slice(8)}</span>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={isCurrent || on}
                      disabled={isCurrent}
                      onChange={() => !isCurrent && toggleApply(d)}
                      data-testid={`apply-day-${DOW[i]}`}
                    />
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-600">
              {applyDates.size === 0
                ? `Saving 1 day (${date}) only.`
                : `Saving ${applyDates.size + 1} days — same operation & shift on each.`}
            </p>
          </div>

          <div>
            <label className="brut-label">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="brut-input mt-1" data-testid="sched-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brut-btn px-3 py-2">Cancel</button>
            <button type="submit" disabled={saving} className="brut-btn brut-btn-primary px-3 py-2" data-testid="save-sched">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
